package com.mps.asc_app.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mps.asc_app.ui.theme.AscGoldLight
import com.mps.asc_app.ui.theme.AscNavyInk
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** Ein Tag im Tagesstreifen der Buchungsseite (Redesign Stufe 2). */
data class StripDay(
    val day: Int,
    val month: Int,
    val year: Int,
    /** 1 = Montag ... 7 = Sonntag */
    val isoWeekday: Int,
    val isToday: Boolean,
) {
    val weekdayShort: String get() = WEEKDAYS_SHORT[isoWeekday - 1]

    fun isSame(day: Int?, month: Int?, year: Int?): Boolean =
        this.day == day && this.month == month && this.year == year
}

private val WEEKDAYS_SHORT = listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So")
private val WEEKDAYS_LONG = listOf("Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag")
private val MONTHS = listOf(
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
)

/** Die nächsten [count] Tage ab heute (heute zuerst). */
fun buildDayStrip(todayDay: Int, todayMonth: Int, todayYear: Int, count: Int = 14): List<StripDay> {
    val today = LocalDate(todayYear, todayMonth, todayDay)
    return (0 until count).map { offset ->
        val d = today.plus(offset, DateTimeUnit.DAY)
        StripDay(d.day, d.month.number, d.year, d.dayOfWeek.isoDayNumber, isToday = offset == 0)
    }
}

/**
 * Länge des Tagesstreifens: mindestens [minDays] Tage ab heute, und so lang, dass ein über den
 * Kalender gewähltes späteres Datum mit einer Woche Luft noch darin liegt.
 */
fun stripLength(
    todayDay: Int, todayMonth: Int, todayYear: Int,
    selectedDay: Int?, selectedMonth: Int?, selectedYear: Int?,
    minDays: Int = 56,
): Int {
    if (selectedDay == null || selectedMonth == null || selectedYear == null) return minDays
    val ahead = LocalDate(todayYear, todayMonth, todayDay).daysUntil(LocalDate(selectedYear, selectedMonth, selectedDay))
    return maxOf(minDays, ahead + 8)
}

/** Überschrift für den gewählten Tag, z. B. "Mittwoch, 4. November". */
fun dayTitle(day: Int, month: Int, year: Int): String {
    val d = LocalDate(year, month, day)
    return "${WEEKDAYS_LONG[d.dayOfWeek.isoDayNumber - 1]}, $day. ${MONTHS[month - 1]}"
}

/**
 * Waagerechter Tagesstreifen auf blauem Grund: Wochentag + Tageszahl, der gewählte Tag in Gold.
 * Ersetzt auf dem Handy den Monatskalender.
 */
@Composable
fun DayStrip(
    days: List<StripDay>,
    selectedDay: Int?,
    selectedMonth: Int?,
    selectedYear: Int?,
    onSelect: (StripDay) -> Unit,
    modifier: Modifier = Modifier,
    onOpenCalendar: (() -> Unit)? = null,
) {
    val selectedIndex = days.indexOfFirst { it.isSame(selectedDay, selectedMonth, selectedYear) }
    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()
    LaunchedEffect(selectedIndex) {
        if (selectedIndex > 2) listState.animateScrollToItem(selectedIndex - 2)
    }
    // Pfeile blättern wochenweise; am Computer lässt sich der Streifen nicht mit der Maus ziehen.
    fun page(delta: Int) {
        scope.launch {
            val target = (listState.firstVisibleItemIndex + delta).coerceIn(0, (days.size - 1).coerceAtLeast(0))
            listState.animateScrollToItem(target)
        }
    }
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically) {
    IconButton(onClick = { page(-7) }, enabled = listState.canScrollBackward) {
        Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = "Eine Woche zurück", tint = Color.White)
    }
    LazyRow(
        state = listState,
        modifier = Modifier.weight(1f),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        itemsIndexed(days) { index, d ->
            val isSelected = index == selectedIndex
            Column(
                modifier = Modifier
                    .width(48.dp)
                    .heightIn(min = 56.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(if (isSelected) AscGoldLight else Color.White.copy(alpha = 0.10f))
                    .semantics {
                        selected = isSelected
                        contentDescription = dayTitle(d.day, d.month, d.year) + if (d.isToday) ", heute" else ""
                    }
                    .clickable(role = Role.Tab) { onSelect(d) }
                    .padding(vertical = 8.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                val content = if (isSelected) AscNavyInk else Color.White
                Text(
                    text = if (d.isToday) "Heute" else d.weekdayShort,
                    color = content.copy(alpha = if (isSelected) 1f else 0.85f),
                    fontSize = 11.sp,
                )
                Text(
                    text = d.day.toString(),
                    color = content,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
    IconButton(onClick = { page(7) }, enabled = listState.canScrollForward) {
        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = "Eine Woche weiter", tint = Color.White)
    }
    if (onOpenCalendar != null) {
        IconButton(onClick = onOpenCalendar) {
            Icon(Icons.Outlined.CalendarMonth, contentDescription = "Datum im Kalender wählen", tint = AscGoldLight)
        }
    }
    }
}
